<template>
    <span :class="classList">
        <input :id="`app-switch-${uuid}`" type="checkbox" :checked="inputValue" @input="toggleInputValue" />
        <label :for="`app-switch-${uuid}`">
            <span class="app-switch__track"></span>
            <span class="app-switch__ball">
                <v-progress-circular
                    class="app-switch__spinner"
                    :width="5"
                    indeterminate
                    :color="inputValue ? 'background' : 'primaryText'"
                />
            </span>
        </label>
    </span>
</template>

<script>
export default {
    name: 'AppSwitch',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        inset: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        inputValue: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        classList() {
            return {
                'app-switch': true,
                'app-switch--checked': this.inputValue,
                'app-switch--disabled': this.disabled,
                'app-switch--readonly': this.readonly,
                'app-switch--loading': this.loading,
                'app-switch--inset': this.inset
            };
        }
    },
    methods: {
        toggleInputValue() {
            if (this.disabled) {
                return;
            }

            if (this.readonly) {
                return;
            }

            if (this.loading) {
                return;
            }

            this.inputValue = !this.inputValue;
        }
    }
};
</script>

<style lang="scss">
$app-switch-height: 1.2rem;
$app-switch-width: 2.6rem;
$app-switch-track-color: var(--v-primaryText-base);
$app-switch-ball-border-width: 0.15rem;
$app-switch-duration: 0.2s;

.app-switch {
    display: inline-flex;

    input[type='checkbox'] {
        visibility: hidden;
        height: 0;
        width: 0;
    }
    label {
        cursor: pointer;
        user-select: none;
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
        height: $app-switch-height;
        width: $app-switch-width;
    }

    &__track {
        height: 100%;
        width: 100%;
        background: $app-switch-track-color;
        border-radius: 10rem;
        transition: all 0.15s ease-in-out;
        opacity: 0.2;
    }

    &__ball {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: $app-switch-ball-border-width;
        left: $app-switch-ball-border-width;
        height: calc($app-switch-height - calc($app-switch-ball-border-width * 2));
        width: calc($app-switch-height - calc($app-switch-ball-border-width * 2));
        border-radius: 100%;
        background: rgba(255, 255, 255, 1);
        box-shadow:
            0px 2px 1px -1px rgba(0, 0, 0, 0.2),
            0px 1px 1px 0px rgba(0, 0, 0, 0.14),
            0px 1px 3px 0px rgba(0, 0, 0, 0.12);
        transition:
            transform $app-switch-duration ease-in-out,
            background 0.15s ease-in-out $app-switch-duration;
    }

    &__spinner {
        visibility: hidden;
        height: 100%;
        width: 100%;
        opacity: 0;
        transition: opacity 0.15s ease-in-out $app-switch-duration;
    }

    &:hover:not(.app-switch--disabled):not(.app-switch--readonly):not(.app-switch--loading) {
        .app-switch__track {
            opacity: 0.3;
        }
    }

    &--disabled {
        label {
            cursor: default;
            opacity: 0.6;
        }
    }

    &--readonly {
        label {
            cursor: default;
        }
    }

    &--loading {
        label {
            cursor: progress;
        }
        .app-switch__ball {
            background: rgba(255, 255, 255, 0);
            box-shadow: none;
        }
        .app-switch__spinner {
            visibility: visible;
            opacity: 1;
        }
    }

    &--inset {
        .app-switch__track {
            height: 0.4rem;
        }
    }

    &--checked {
        .app-switch__track {
            background: var(--v-success-base);
            opacity: 1;
        }
        .app-switch__ball {
            transform: translateX(calc($app-switch-width - $app-switch-height - ($app-switch-ball-border-width/2)));
        }

        &:hover:not(.app-switch--disabled):not(.app-switch--readonly):not(.app-switch--loading) {
            .app-switch__track {
                opacity: 0.9;
            }
        }
    }
}
</style>
